import type React from "react";
import { useState } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { ActivityType } from "api/services/treatment/schemas/ScheduleWeek";
import { useLocalizedDate } from "utils/date";
import { translateActivityType } from "utils/translations";

import { CommentDialog } from "../ProtocolTimeline/CommentDialog";
import type { ProtocolCard } from "../ProtocolTimeline/types";
import { isActivity, isExercise, isLesson } from "../ProtocolTimeline/utils";
import { CommentButton } from "../shared";

interface CalendarCardProps {
  activity: ProtocolCard;
  smartReply: string | null | undefined;
  smartReplyId?: number | null | undefined;
  completed?: string | null;
  current_level?: number | null;
  isPast?: boolean;
  max_level?: number | null;
  activity_type: ActivityType;
  onPreview?: () => void;
}

export const CalendarCard: React.FC<CalendarCardProps> = ({
  activity,
  smartReply,
  smartReplyId,
  activity_type = "exercise",
  max_level = null,
  current_level = null,
  completed = null,
  isPast = false,
  onPreview,
}) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const [showComment, setShowComment] = useState(false);

  validateLevels({ max_level, current_level, activity_type });
  validateCurrentLevel({ max_level, current_level, activity_type });

  const getClassName = () => {
    if (completed) {
      return "completed";
    }
    if (isPast) {
      return "disabled";
    }

    return "";
  };

  return (
    <>
      <Container onClick={onPreview} className={getClassName()} data-testid="calendar-card" $schedulerVersion={1}>
        {(isExercise(activity) || isLesson(activity)) && activity.image_url && (
          <ExerciseImage src={activity.image_url} alt={activity.title} />
        )}

        <Content>
          <TitleRow>
            <Title>{activity.title}</Title>
            {isActivity(activity) && activity.required && <RequiredDot title="Required" />}
          </TitleRow>

          <MetaRow>
            <TypeLabel>{translateActivityType(activity_type)}</TypeLabel>
            {isExercise(activity) && activity.current_level && activity.max_level && (
              <>
                <Separator>•</Separator>
                <Level>{t("activities.level", { current: activity.current_level, total: activity.max_level })}</Level>
              </>
            )}
            {completed && (
              <CompletedDate>
                {t("activities.completed")} {format(parseISO(completed), "MMM d")}
              </CompletedDate>
            )}
          </MetaRow>
        </Content>

        <Actions>
          {activity.comment && (
            <CommentButton hasReply={Boolean(activity.reply)} onClick={() => setShowComment(true)} />
          )}
        </Actions>
      </Container>

      {showComment && activity.comment && activity.patient_id && activity.user_activity_id && (
        <CommentDialog
          activity={activity}
          smartReply={smartReply}
          smartReplyId={smartReplyId}
          isOpen={showComment}
          setIsOpen={setShowComment}
        />
      )}
    </>
  );
};

const validateLevels = (props: {
  max_level: number | null;
  current_level: number | null;
  activity_type: ActivityType;
}) => {
  const { max_level, current_level, activity_type } = props;
  const propName = "max_level";
  const componentName = "CalendarCard";

  if (activity_type === "exercise" && max_level === null) {
    return new Error(`Missing prop, max_level is required for type 'exercise' in ${componentName}`);
  }
  if (max_level && typeof max_level !== "number") {
    return new Error(`Invalid prop ${propName} supplied to ${componentName}, expected number`);
  }
  if (max_level && current_level && current_level > max_level) {
    return new Error(`Invalid props, current_level need to be smaller or equal to max_level in ${componentName}`);
  }
  return null;
};

const validateCurrentLevel = (props: {
  max_level: number | null;
  current_level: number | null;
  activity_type: ActivityType;
}) => {
  const { current_level, max_level, activity_type } = props;
  const propName = "current_level";
  const componentName = "CalendarCard";

  if (activity_type === "exercise" && current_level === null) {
    return new Error(`Missing prop, current_level is required for type 'exercise' in ${componentName}`);
  }
  if (current_level && typeof current_level !== "number") {
    return new Error(`Invalid prop ${propName} supplied to ${componentName}, expected number`);
  }
  if (max_level && current_level && current_level > max_level) {
    return new Error(`Invalid props, current_level need to be smaller or equal to max_level in ${componentName}`);
  }
  return null;
};

export const Container = styled.div<{ $schedulerVersion: number }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 90px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.semantic.container.background};
  border: ${({ theme }) => theme.semantic.container.border};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: ${({ $schedulerVersion }) => ($schedulerVersion === 1 ? "pointer" : "grab")};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &.completed {
    background-color: #f0f9f4;
    border-color: #4caf50;
    cursor: ${({ $schedulerVersion }) => ($schedulerVersion === 1 ? "pointer" : "not-allowed")};

    &:hover {
      border-color: #388e3c;
      box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
    }
  }

  &.disabled {
    cursor: ${({ $schedulerVersion }) => ($schedulerVersion === 1 ? "pointer" : "not-allowed")};
  }

  &.dragging {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    opacity: 0.8;
  }
`;

export const ExerciseImage = styled.img`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 6px;
  object-fit: cover;
`;

export const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a2332;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const RequiredDot = styled.div`
  width: 6px;
  height: 6px;
  background-color: #ff9800;
  border-radius: 50%;
  flex-shrink: 0;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
`;

export const TypeLabel = styled.span`
  color: #5c6f82;
  font-weight: 500;
`;

export const Separator = styled.span`
  color: #c3cfdc;
  font-weight: 700;
  font-size: 14px;
`;

export const Level = styled.span`
  color: #5c6f82;
  font-weight: 500;
`;

export const CompletedDate = styled.span`
  color: #4caf50;
  font-weight: 500;
`;

export const Actions = styled.div`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ActionButton = styled.button<{ $variant: "secondary" | "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: ${({ theme, $variant }) => {
    if ($variant === "danger") return theme.semantic.container.border;
    return theme.semantic.container.border;
  }};
  background-color: #ffffff;
  color: ${({ $variant }) => {
    if ($variant === "danger") return "#d32f2f";
    return "#5c6f82";
  }};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${({ $variant }) => {
      if ($variant === "danger") return "#ffebee";
      return "#f3f5f7";
    }};
    border-color: ${({ $variant }) => {
      if ($variant === "danger") return "#d32f2f";
      return "#c5d0dd";
    }};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
